import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailService } from '@/services/emailService';
import type { DisparoEmail, DisparoEmailStatus, DeliveryStatus } from '@/types/email';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';

function statusVariant(status: DisparoEmailStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'ENVIANDO': return 'default';
    case 'PAUSADO': return 'secondary';
    case 'CANCELADO': return 'destructive';
    case 'CONCLUIDO': return 'outline';
    default: return 'secondary'; // RASCUNHO
  }
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('pt-BR'); } catch { return String(v); }
}

const DELIVERY_LABEL: Record<DeliveryStatus, string> = {
  SENT: 'Enviado', DELIVERED: 'Entregue', OPENED: 'Aberto',
  BOUNCED: 'Bounce', COMPLAINED: 'Spam',
};
function deliveryVariant(s: string | null): 'outline' | 'destructive' | 'secondary' {
  if (s === 'BOUNCED' || s === 'COMPLAINED') return 'destructive';
  if (s === 'DELIVERED' || s === 'OPENED') return 'secondary';
  return 'outline';
}

function extractErro(e: unknown): string {
  const err = e as { response?: { data?: { error?: string } }; message?: string } | null;
  return err?.response?.data?.error || err?.message || 'Erro';
}

interface EmailLotesPanelProps {
  clientId: number | null;
}

export function EmailLotesPanel({ clientId }: EmailLotesPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [contatosDe, setContatosDe] = useState<DisparoEmail | null>(null);
  const [enviarAlvo, setEnviarAlvo] = useState<DisparoEmail | null>(null);

  const { data: lotes = [], isLoading } = useQuery({
    queryKey: ['email-lotes', clientId],
    queryFn: () => emailService.listCampanhas(clientId ?? undefined),
    enabled: clientId != null,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['email-lotes', clientId] });

  const iniciarMut = useMutation({
    mutationFn: (id: number) => emailService.iniciarCampanha(id),
    onSuccess: () => { toast({ title: 'Envio iniciado' }); invalidar(); },
    onError: (e: unknown) => toast({ title: 'Não foi possível iniciar', description: extractErro(e), variant: 'destructive' }),
  });

  const cancelarMut = useMutation({
    mutationFn: (id: number) => emailService.cancelarCampanha(id),
    onSuccess: () => { toast({ title: 'Envio cancelado' }); invalidar(); },
    onError: (e: unknown) => toast({ title: 'Não foi possível cancelar', description: extractErro(e), variant: 'destructive' }),
  });

  const pausarMut = useMutation({
    mutationFn: (id: number) => emailService.pausarCampanha(id),
    onSuccess: () => { toast({ title: 'Envio pausado' }); invalidar(); },
    onError: (e: unknown) => toast({ title: 'Não foi possível pausar', description: extractErro(e), variant: 'destructive' }),
  });

  const retomarMut = useMutation({
    mutationFn: (id: number) => emailService.retomarCampanha(id),
    onSuccess: () => { toast({ title: 'Envio retomado' }); invalidar(); },
    onError: (e: unknown) => toast({ title: 'Não foi possível retomar', description: extractErro(e), variant: 'destructive' }),
  });

  const reenviarFalhasMut = useMutation({
    mutationFn: (id: number) => emailService.reenviarFalhasCampanha(id),
    onSuccess: () => { toast({ title: 'Reenviando falhas' }); invalidar(); },
    onError: (e: unknown) => toast({ title: 'Não foi possível reenviar', description: extractErro(e), variant: 'destructive' }),
  });

  const { data: contatosData } = useQuery({
    queryKey: ['email-lote-contatos', contatosDe?.id],
    queryFn: () => emailService.getCampanhaContatos(contatosDe!.id, 1, 200),
    enabled: !!contatosDe,
  });

  const { data: entregaResumo } = useQuery({
    queryKey: ['email-lote-entrega', contatosDe?.id],
    queryFn: () => emailService.getCampanhaEntregaResumo(contatosDe!.id),
    enabled: !!contatosDe,
  });

  if (clientId == null) {
    return <p className="text-sm text-muted-foreground">Selecione um cliente para ver os lotes de email.</p>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Lotes de email</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : lotes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum lote de email para este cliente.</p>
          ) : (
            <div className="space-y-2">
              {lotes.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-4 border rounded-lg p-3">
                  <div>
                    <p className="font-medium">{c.nome}</p>
                    <p className="text-sm text-muted-foreground">
                      {c.total_contatos} contato(s) · {c.enviados} enviado(s) · {c.falhas} falha(s) · {fmtDateTime(c.created_at)}
                    </p>
                    {c.origem_lote_id != null && (
                      <span className="text-xs text-gray-500">Gerado do lote #{c.origem_lote_id}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={statusVariant(c.status)}>{c.status}</Badge>
                    {c.status === 'RASCUNHO' && (
                      <Button size="sm" onClick={() => setEnviarAlvo(c)}>Enviar</Button>
                    )}
                    {c.status === 'ENVIANDO' && (
                      <>
                        <Button size="sm" variant="secondary" onClick={() => pausarMut.mutate(c.id)} disabled={pausarMut.isPending && pausarMut.variables === c.id}>Pausar</Button>
                        <Button size="sm" variant="destructive" onClick={() => cancelarMut.mutate(c.id)} disabled={cancelarMut.isPending && cancelarMut.variables === c.id}>Cancelar</Button>
                      </>
                    )}
                    {c.status === 'PAUSADO' && (
                      <>
                        <Button size="sm" onClick={() => retomarMut.mutate(c.id)} disabled={retomarMut.isPending && retomarMut.variables === c.id}>Retomar</Button>
                        <Button size="sm" variant="destructive" onClick={() => cancelarMut.mutate(c.id)} disabled={cancelarMut.isPending && cancelarMut.variables === c.id}>Cancelar</Button>
                      </>
                    )}
                    {c.status === 'CANCELADO' && (
                      <Button size="sm" onClick={() => retomarMut.mutate(c.id)} disabled={retomarMut.isPending && retomarMut.variables === c.id}>Retomar</Button>
                    )}
                    {(c.status === 'CONCLUIDO' || c.status === 'CANCELADO' || c.status === 'PAUSADO') && c.falhas > 0 && (
                      <Button size="sm" variant="outline" onClick={() => reenviarFalhasMut.mutate(c.id)} disabled={reenviarFalhasMut.isPending && reenviarFalhasMut.variables === c.id}>Reenviar falhas</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setContatosDe(c)}>Ver contatos</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!enviarAlvo} onOpenChange={(o) => { if (!o) setEnviarAlvo(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Iniciar envio por email?</AlertDialogTitle>
            <AlertDialogDescription>
              {enviarAlvo ? `Serão enviados ${enviarAlvo.total_contatos} email(s) do lote "${enviarAlvo.nome}".` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const alvo = enviarAlvo;
                setEnviarAlvo(null);
                if (alvo) iniciarMut.mutate(alvo.id);
              }}
            >
              Enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!contatosDe} onOpenChange={(o) => { if (!o) setContatosDe(null); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Contatos — {contatosDe?.nome}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Exibindo {contatosData?.contatos.length ?? 0} de {contatosData?.total ?? 0} contato(s)
          </p>
          {entregaResumo && (
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-2">
              <span>Total: <b>{entregaResumo.total}</b></span>
              <span>Entregues: <b>{entregaResumo.entregues}</b></span>
              <span>Abertos: <b>{entregaResumo.abertos}</b></span>
              <span>Bounces: <b>{entregaResumo.bounces}</b></span>
              <span>Spam: <b>{entregaResumo.complaints}</b></span>
            </div>
          )}
          <div className="max-h-[60vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Enviado em</TableHead>
                  <TableHead>Entrega</TableHead>
                  <TableHead>Aberto em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(contatosData?.contatos ?? []).map((ct) => (
                  <TableRow key={ct.id}>
                    <TableCell className="font-mono text-xs">{ct.email_destino}</TableCell>
                    <TableCell>
                      <Badge variant={ct.status_envio === 'FALHA' || ct.status_envio === 'EMAIL_INVALIDO' ? 'destructive' : 'outline'}>{ct.status_envio}</Badge>
                      {ct.motivo_falha && <span className="ml-2 text-xs text-muted-foreground">{ct.motivo_falha}</span>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(ct.enviado_at)}</TableCell>
                    <TableCell>
                      {ct.delivery_status ? (
                        <Badge variant={deliveryVariant(ct.delivery_status)}>
                          {DELIVERY_LABEL[ct.delivery_status] ?? ct.delivery_status}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                      {ct.falha_entrega_motivo && (
                        <span className="ml-2 text-xs text-muted-foreground">{ct.falha_entrega_motivo}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(ct.aberto_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EmailLotesPanel;
